import SectionCard from "@/components/common/SectionCard";
import { Skeleton } from "@/components/ui/skeleton";

interface NotificationsSkeletonProps {
  /** Day buckets to render, and how many rows sit under each. */
  groups?: number[];
}

// Mirrors NotificationRow: avatar with a status dot, title + description +
// record label stack, trailing timestamp. Widths vary so it reads as text.
const TITLES = ["w-44", "w-56", "w-40", "w-52"];
const BODIES = ["w-72", "w-64", "w-80", "w-60"];

export default function NotificationsSkeleton({ groups = [3, 2] }: NotificationsSkeletonProps) {
  return (
    <div role="status" aria-label="Loading notifications" data-testid="notifications-skeleton">
      {groups.map((rows, groupIndex) => (
        <div key={groupIndex} className="mb-6">
          <div className="mb-3 flex items-center gap-3">
            <Skeleton className="h-4 w-24" />
            <div className="h-px flex-1 bg-dash-border" />
          </div>

          <SectionCard contentClassName="p-0">
            {Array.from({ length: rows }, (_, row) => (
              <div
                key={row}
                className="flex w-full items-start gap-4 border-b border-dash-border px-4 py-4 last:border-b-0 sm:px-6"
              >
                <div className="relative shrink-0">
                  <Skeleton className="size-9 rounded-full" />
                  <Skeleton className="absolute -bottom-1 -right-1 size-4 rounded-full ring-2 ring-white" />
                </div>

                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className={`h-4 max-w-full ${TITLES[row % TITLES.length]}`} />
                  <Skeleton className={`h-3.5 max-w-full ${BODIES[row % BODIES.length]}`} />
                  <Skeleton className="h-3 w-32" />
                </div>

                <Skeleton className="h-3 w-12 shrink-0" />
              </div>
            ))}
          </SectionCard>
        </div>
      ))}
      <span className="sr-only">Loading notifications…</span>
    </div>
  );
}
